/**
 * Responsibility: Authority for user session state, authentication flows, and access control.
 * Scope: Singleton manager; encapsulates auth interaction and initial routing.
 */
import { authStore } from './ui/stores/auth-store'
import { adminStore, type AdminAccessStatus } from './ui/stores/admin-store'
import { navigateState, stateService, dispatcher, registerAuthGuard } from './app-states'
import { TOPICS } from "@anyquest/shared-core"

function isKnownSignedInPath(pathname: string): boolean {
  if (!pathname || pathname === '/') return false
  return [
    /^\/welcome\/?$/i,
    /^\/hub\/?$/i,
    /^\/marketplace\/?$/i,
    /^\/saves\/?$/i,
    /^\/worlds\/?$/i,
    /^\/vessels\/?$/i,
    /^\/pairing\/?$/i,
    /^\/import\/?$/i,
    /^\/new\/?$/i,
    /^\/admin\/?$/i,
    /^\/dev\/?$/i,
    /^\/play\/[^/]+\/?$/i,
    /^\/error\/?$/i,
  ].some((pattern) => pattern.test(pathname))
}

function isInviteeSignedInPath(pathname: string): boolean {
  if (!pathname || pathname === '/') return false
  return [
    /^\/welcome\/?$/i,
    /^\/play\/[^/]+\/?$/i,
    /^\/error\/?$/i,
  ].some((pattern) => pattern.test(pathname))
}

export interface UserSession {
  status: 'loading' | 'signed-in' | 'signed-out' | 'blocked'
  uid: string | null
  email: string | null
  displayName: string | null
  photoURL: string | null
  isAdmin: boolean
  adminStatus: AdminAccessStatus
  initials: string
}

class SessionManager {
  private initialized = false
  public user: UserSession = {
    status: 'loading',
    uid: null,
    email: null,
    displayName: null,
    photoURL: null,
    isAdmin: false,
    adminStatus: 'loading',
    initials: '?'
  }
  constructor() {
    // Authority: Listen to underlying stores and update the canonical session object
    authStore.subscribe(() => this.refresh())
    adminStore.subscribe(() => this.refresh())
    
    // Enforcement: Register the global route guard
    registerAuthGuard(() => this.user.status)
  }

  /**
   * Explicitly trigger initial access enforcement.
   * Should be called AFTER AppShell has registered its routing listeners.
   */
  public init() {
    if (this.initialized) return
    this.initialized = true
    this.refresh()
  }

  /**
   * Primary Login Flow: Google
   */
  public async loginWithGoogle() {
    try {
      await authStore.signInWithGoogle()
    } catch (err) {
      console.error('[Session] Login failed:', err)
      throw err
    }
  }

  /**
   * Primary Logout Flow
   */
  public async logout() {
    await authStore.signOut()
    navigateState('login')
  }

  /**
   * Refresh the canonical session object and handle necessary redirects
   */
  private async refresh() {
    const user = authStore.user
    const admin = adminStore.snapshot
    const status = authStore.status
    
    const name = user?.displayName ?? user?.email ?? '?'
    
    this.user = {
      status,
      uid: user?.uid ?? null,
      email: user?.email ?? null,
      displayName: user?.displayName ?? null,
      photoURL: null,
      isAdmin: admin.isAdmin,
      adminStatus: admin.status,
      initials: name.slice(0, 2).toUpperCase()
    }

    // Broadcast change to UI
    dispatcher.trigger('session', TOPICS.auth.signedIn, { session: this.user })

    if (this.initialized) {
      this.enforceAccess()
    }
  }

  /**
   * Business Logic: Access Control & Initial Routing
   */
  private async enforceAccess() {
    if (this.user.status === 'loading') return
    if (this.user.status === 'blocked') {
      if (stateService.getState()?.name !== 'login') navigateState('login')
      return
    }
    if (this.user.adminStatus === 'loading') return

    const currentState = stateService.getState()?.name
    const pathname = typeof window !== 'undefined' ? String(window.location.pathname || '').trim() : ''
    const hasExplicitSignedInPath = pathname !== '' && pathname !== '/'
    const hasKnownSignedInPath = isKnownSignedInPath(pathname)
    const hasInviteeSignedInPath = isInviteeSignedInPath(pathname)
    const fallbackState = this.user.isAdmin ? 'hub' : 'welcome'

    if (this.user.status === 'signed-in') {
      // Closed Beta Gate
      const isSupreme = this.user.email === 'dharmax@gmail.com'
      if (!isSupreme) {
        const entitlement = await authStore.fetchEntitlement()
        if (entitlement?.status === 403) {
          dispatcher.trigger('session', TOPICS.ui.notify, { message: entitlement.error, level: 'error' })
          if (currentState !== 'login') navigateState('login')
          return
        }
      }

      // Entry Redirect
      // Preserve direct signed-in routes like /new or /play/:id until the router resolves them.
      if (!currentState) {
        if (!hasExplicitSignedInPath || !hasKnownSignedInPath) navigateState(fallbackState)
        return
      }
      if (currentState === 'login' && (!hasExplicitSignedInPath || !hasKnownSignedInPath)) {
        navigateState(fallbackState)
        return
      }
      const allowWelcomeDebug = typeof window !== 'undefined' && window.sessionStorage.getItem('am2_welcome_debug') === '1'
      if (this.user.isAdmin && currentState === 'welcome' && pathname === '/welcome' && allowWelcomeDebug) {
        return
      }
      if (this.user.isAdmin && (currentState === 'welcome' || pathname === '/welcome')) {
        navigateState('hub')
        return
      }
      if (!this.user.isAdmin && hasExplicitSignedInPath && !hasInviteeSignedInPath) {
        navigateState('welcome')
      }
    } else {
      // Not signed in
      if (currentState && currentState !== 'login') {
        navigateState('login')
      }
    }
  }
}

export const sessionManager = new SessionManager()
